import { Box, Button, Chip, CircularProgress, MenuItem, Paper, TextField, Typography } from "@mui/material";
import PasswordField from "../../components/PasswordField";
import { APP_NAME } from '../../constants/brand';
import LoginSideImage from '../../images/login/login-side-register.png';
import LogoImage from '../../images/logo/logo.png';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import Fetch from "../../services/Fetch";
import dayjs from "dayjs";
import SnackbarAlert from "../../components/SnackBar";
import AuthContext from "../../context/AuthContext";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { buildRegisterFormData } from "../../helper/RegisterFormData";

const fieldSx = {
     '& .MuiOutlinedInput-root': {
          borderRadius: 2.5,
          bgcolor: 'background.paper',
     },
};

function Register() {
     const host = `${process.env.REACT_APP_LOCAL_HOST}`;
     const { wait } = useContext(AuthContext);
     const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
     const { sendWait, setSendWait } = useWaits();
     const [firstName, setFirstName] = useState('');
     const [middleName, setMiddleName] = useState('');
     const [lastName, setLastName] = useState('');
     const [email, setEmail] = useState('');
     const [phoneCode, setPhoneCode] = useState('');
     const [phoneNumber, setPhoneNumber] = useState('');
     const [birthDate, setBirthDate] = useState('');
     const [password, setPassword] = useState('');
     const [passwordConfirmation, setPasswordConfirmation] = useState('');
     const [academicDegreeId, setAcademicDegreeId] = useState('');
     const [majorId, setMajorId] = useState('');
     const [degrees, setDegrees] = useState([]);
     const [majors, setMajors] = useState([]);
     const navigate = useNavigate();

     useEffect(() => {
          const loadLists = async () => {
               const [d, m] = await Promise.all([
                    Fetch(host + '/academic-degrees?per_page=100', 'GET'),
                    Fetch(host + '/majors?per_page=200', 'GET'),
               ]);
               if (d.status === 200) setDegrees(d.data.data || []);
               if (m.status === 200) setMajors(m.data.data || []);
          };
          loadLists();
     }, [host]);

     const handleChange = (value, country) => {
          const numberWithoutCode = value.replace(country.dialCode, "");
          setPhoneCode(country.dialCode);
          setPhoneNumber(numberWithoutCode);
     };

     const selectedDegree = degrees.find((d) => String(d.id) === String(academicDegreeId));
     const degreeLevel = selectedDegree?.level || '';
     const filteredMajors = majors.filter((m) => {
          if (degreeLevel && m.level && m.level !== degreeLevel) return false;
          return true;
     });

     const register = async () => {
          if (!firstName.trim() || !lastName.trim() || !email.trim()) {
               setSnackBar("error", "أكمل الحقول المطلوبة");
               return;
          }
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
               setSnackBar("error", "صيغة البريد غير صحيحة");
               return;
          }
          if (password.length < 6) {
               setSnackBar("error", "كلمة المرور 6 أحرف على الأقل");
               return;
          }
          if (password !== passwordConfirmation) {
               setSnackBar("error", "كلمتا المرور غير متطابقتين");
               return;
          }

          setSendWait(true);

          const formData = buildRegisterFormData({
               firstName,
               middleName,
               lastName,
               phoneCode,
               phoneNumber,
               birthDate,
               email,
               password,
               passwordConfirmation,
               accountRole: "student",
               majorId,
               academicDegreeId,
          });

          const result = await Fetch(host + '/register', 'POST', formData);

          if (result.status === 200) {
               localStorage.setItem('token', result.data.data.token);
               localStorage.setItem('language', result.data.data.user.language || "ar");
              sessionStorage.setItem('student_verify_auto_resend', '1');
               navigate('/verify');
          } else if (result.status === 422) {
               setSnackBar("error", result.data.errors[0]);
          } else {
               setSnackBar("error", result.data?.message || "تعذر إنشاء الحساب");
          }

          setSendWait(false);
     };

     return (
          <>
               {wait ? (
                    <Box className="w-full h-screen relative flex justify-center items-center">
                         <CircularProgress size={70} />
                    </Box>
               ) : (
                    <Box
                         sx={{
                              minHeight: '100vh',
                              background: (theme) => theme.palette.mode === 'dark'
                                   ? theme.palette.background.default
                                   : 'linear-gradient(180deg, #eef4ff 0%, #f8fbff 100%)',
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', lg: '1fr minmax(520px, 620px)' },
                         }}
                    >
                         <Box
                              sx={{
                                   display: { xs: 'none', lg: 'block' },
                                   position: 'relative',
                                   overflow: 'hidden',
                              }}
                         >
                              <Box
                                   component="img"
                                   src={LoginSideImage}
                                   alt="Student onboarding illustration"
                                   sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                   }}
                              />
                              <Box
                                   sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        background: 'linear-gradient(145deg, rgba(13,53,140,0.8) 0%, rgba(29,78,216,0.45) 45%, rgba(15,23,42,0.6) 100%)',
                                   }}
                              />
                              <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', alignItems: 'flex-end', p: 6 }}>
                                   <Box sx={{ maxWidth: 460, color: 'white' }}>
                                        <Typography variant="h3" fontWeight={900} sx={{ lineHeight: 1.15, mb: 2 }}>
                                             ابدأ رحلتك التعليمية بثقة
                                        </Typography>
                                        <Typography sx={{ opacity: 0.92, lineHeight: 1.9 }}>
                                             أنشئ حساب الطالب للوصول إلى الدورات والاختبارات والملفات وسجل التقدم من مكان واحد بتصميم أوضح وأسهل.
                                        </Typography>
                                   </Box>
                              </Box>
                         </Box>

                         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 3, md: 4 } }}>
                              <Paper
                                   elevation={0}
                                   sx={{
                                        width: '100%',
                                        maxWidth: 620,
                                        borderRadius: { xs: 4, md: '40px 0 0 40px' },
                                        overflow: 'hidden',
                                        border: '1px solid',
                                        borderColor: 'rgba(148,163,184,0.22)',
                                        bgcolor: 'background.paper',
                                        boxShadow: '0 24px 60px rgba(15,23,42,0.12)',
                                   }}
                              >
                                   <Box sx={{ p: { xs: 3, sm: 4, md: 5 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                                             <Chip
                                                  label="حساب طالب جديد"
                                                  sx={{ bgcolor: 'rgba(13,53,140,0.1)', color: '#0D358C', fontWeight: 700 }}
                                             />
                                             <Box className="auth-logo-card" sx={{ m: 0 }}>
                                                  <Box component="img" src={LogoImage} alt={APP_NAME} className="auth-logo" />
                                             </Box>
                                        </Box>

                                        <Box>
                                             <Typography variant="h4" fontWeight={900} sx={{ color: '#0D358C', lineHeight: 1.15, fontSize: { xs: '1.9rem', sm: '2.25rem' } }}>
                                                  إنشاء حساب
                                             </Typography>
                                             <Typography variant="body1" sx={{ mt: 1.25, color: 'text.secondary', lineHeight: 1.8 }}>
                                                  أنشئ حساب الطالب للوصول إلى الدروس والاختبارات وتتبع تقدمك بسهولة.
                                             </Typography>
                                        </Box>

                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                                             <TextField value={lastName} onChange={(e) => setLastName(e.target.value)} label="اسم العائلة" fullWidth sx={fieldSx} />
                                             <TextField value={middleName} onChange={(e) => setMiddleName(e.target.value)} label="اسم الأب" fullWidth sx={fieldSx} />
                                             <TextField value={firstName} onChange={(e) => setFirstName(e.target.value)} label="الاسم الأول" fullWidth sx={fieldSx} />
                                        </Box>

                                        <Box sx={{ display: 'grid', gap: 2.5 }}>
                                             <Box>
                                                  <Typography variant="subtitle2" sx={{ mb: 1.25, color: '#0D358C', fontWeight: 800 }}>
                                                       بيانات التواصل
                                                  </Typography>
                                                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
                                                       <Box sx={{ minWidth: 0 }}>
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
                                                                 رقم الهاتف
                                                            </Typography>
                                                            <PhoneInput
                                                                 country={'us'}
                                                                 value={`${phoneCode || ''}${phoneNumber || ''}`}
                                                                 onChange={handleChange}
                                                                 containerStyle={{ width: '100%' }}
                                                                 inputStyle={{
                                                                      width: '100%',
                                                                      height: '56px',
                                                                      borderRadius: '12px',
                                                                      borderColor: '#cbd5e1',
                                                                      paddingLeft: '52px',
                                                                 }}
                                                                 buttonStyle={{
                                                                      borderTopLeftRadius: '12px',
                                                                      borderBottomLeftRadius: '12px',
                                                                      borderColor: '#cbd5e1',
                                                                 }}
                                                            />
                                                       </Box>

                                                       <Box sx={{ mt: { xs: 0, sm: 1 } }}>
                                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                 <DatePicker
                                                                      value={birthDate ? dayjs(birthDate) : null}
                                                                      onChange={(value) => setBirthDate(value ? dayjs(value).format("YYYY-MM-DD") : '')}
                                                                      label="تاريخ الميلاد"
                                                                      slotProps={{
                                                                           textField: {
                                                                                fullWidth: true,
                                                                                sx: fieldSx,
                                                                           },
                                                                      }}
                                                                 />
                                                            </LocalizationProvider>
                                                       </Box>

                                                       <Box sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}>
                                                            <TextField value={email} onChange={(e) => setEmail(e.target.value)} label="البريد الإلكتروني" type="email" fullWidth sx={fieldSx} />
                                                       </Box>
                                                  </Box>
                                             </Box>

                                             <Box>
                                                  <Typography variant="subtitle2" sx={{ mb: 1.25, color: '#0D358C', fontWeight: 800 }}>
                                                       بيانات الحساب والدراسة
                                                  </Typography>
                                                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                                       <PasswordField onChange={(e) => setPasswordConfirmation(e.target.value)} value={passwordConfirmation} label="تأكيد كلمة المرور" fullWidth sx={fieldSx} />
                                                       <PasswordField onChange={(e) => setPassword(e.target.value)} value={password} label="كلمة المرور" fullWidth sx={fieldSx} />
                                                       <TextField
                                                            select
                                                            label="التخصص"
                                                            value={majorId}
                                                            onChange={(e) => setMajorId(e.target.value)}
                                                            fullWidth
                                                            sx={fieldSx}
                                                       >
                                                            {filteredMajors.map((m) => (
                                                                 <MenuItem key={m.id} value={m.id}>{m.name_ar || m.name_en}</MenuItem>
                                                            ))}
                                                       </TextField>
                                                       <TextField
                                                            select
                                                            label="المرحلة"
                                                            value={academicDegreeId}
                                                            onChange={(e) => {
                                                                 setAcademicDegreeId(e.target.value);
                                                                 setMajorId('');
                                                            }}
                                                            fullWidth
                                                            sx={fieldSx}
                                                       >
                                                            {degrees.map((d) => (
                                                                 <MenuItem key={d.id} value={d.id}>{d.name_ar || d.name_en}</MenuItem>
                                                            ))}
                                                       </TextField>
                                                  </Box>
                                             </Box>
                                        </Box>

                                        <Button
                                             variant="contained"
                                             size="large"
                                             onClick={register}
                                             disabled={sendWait}
                                             sx={{
                                                  mt: 0.5,
                                                  minHeight: 52,
                                                  borderRadius: 3,
                                                  bgcolor: '#0D358C',
                                                  fontWeight: 800,
                                                  boxShadow: '0 12px 30px rgba(13,53,140,0.25)',
                                                  '&:hover': { bgcolor: '#1d4ed8' },
                                             }}
                                        >
                                             {sendWait ? <CircularProgress size={22} color="inherit" /> : "إنشاء الحساب"}
                                        </Button>

                                        <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: '#e2e8f0' }}>
                                             <Typography
                                                  variant="body2"
                                                  sx={{ color: '#0D358C', cursor: 'pointer', fontWeight: 700 }}
                                                  onClick={() => navigate('/login')}
                                             >
                                                  لدي حساب بالفعل
                                             </Typography>
                                        </Box>
                                   </Box>
                              </Paper>
                         </Box>

                         <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
                    </Box>
               )}
          </>
     );
}

export default Register;